import { MouseEventHandler, useCallback, useState } from "react";

import { useDom } from "../../../../context/Dom";
import { Position } from "../../types";
import {
  useAutoCloseOnClick,
  useContextPopupState,
  useElementDimensions
} from "./hooks";
import { Dimensions } from "./types";

type AnchorRenderProps = {
  openPopup: () => void;
  closePopup: () => void;
  isOpen: boolean;
  anchorDimensions: Dimensions;
  popupDimensions: Dimensions;
};

type PopupRenderProps = {
  closePopup: () => void;
  isOpen: boolean;
};

type AnchorProps =
  | {
      onClick: MouseEventHandler;
      onMouseEnter: MouseEventHandler;
      onMouseLeave: MouseEventHandler;
    }
  | {
      onClick: MouseEventHandler;
    };

type Props = {
  position: Position;
  closeOnPopupClick?: boolean;
  hover?: boolean;
  closeDelay?: number;
  // Only used when hover = true
  children: {
    anchor: (
      props: AnchorRenderProps & {
        anchorProps: AnchorProps;
      }
    ) => React.ReactNode;
    popup: (props: PopupRenderProps) => React.ReactNode;
  };
  anchorWidth?: string; // allows the parent to override the width of the anchor e.g. filling parent's width
  anchorHeight?: string; // allows the parent to override the height of the anchor e.g. filling parent's height
  boundingElementRef?: React.MutableRefObject<HTMLElement | null>;
  enablePopup?: boolean;
};

export default function ContextPopup({
  position = "bottom",
  children,
  closeOnPopupClick = true,
  hover = false,
  closeDelay = 200,
  anchorWidth = "max-content",
  anchorHeight = "max-content",
  boundingElementRef,
  enablePopup = true
}: Props) {
  const {
    openPopup,
    closePopup,
    isOpen,
    anchor,
    popup,
    anchorRef,
    setPopupDimensions,
    anchorProps,
    popupProps
  } = useContextPopupState({
    position,
    hover,
    closeDelay,
    boundingElementRef,
    enabled: enablePopup
  });

  const childProps = {
    isOpen,
    openPopup,
    closePopup,
    anchorDimensions: anchor,
    popupDimensions: popup
  };

  const { createPortal } = useDom();

  return (
    <div style={{ height: anchorHeight }}>
      <div
        ref={anchorRef}
        style={{
          position: "relative",
          width: anchorWidth,
          height: anchorHeight
        }}
      >
        {children.anchor({ ...childProps, anchorProps })}
      </div>
      {isOpen &&
        createPortal(
          <Popup
            {...childProps}
            popupProps={popupProps}
            onLayout={setPopupDimensions}
            top={popup.top}
            left={popup.left}
            closeOnPopupClick={closeOnPopupClick}
          >
            {children.popup}
          </Popup>,
          anchorRef.current
        )}
    </div>
  );
}

type PopupProps = {
  children: (props: PopupRenderProps) => React.ReactNode;
  top: number;
  left: number;
  onLayout: (dimensions: any) => void;
  popupProps: any;
  closeOnPopupClick: boolean;
  closePopup: () => void;
  isOpen: boolean;
};

function Popup({
  children,
  top = 0,
  left = 0,
  onLayout,
  popupProps,
  closeOnPopupClick,
  ...rest
}: PopupProps) {
  const [rendered, setRendered] = useState(false);

  const handleLayout = useCallback(
    (dimensions: any) => {
      if (rendered) onLayout(dimensions);
      setRendered(true);
    },
    [rendered, onLayout]
  );

  const ref = useElementDimensions(handleLayout);

  useAutoCloseOnClick({
    isOpen: rest.isOpen,
    closePopup: rest.closePopup,
    closeOnPopupClick,
    popupRef: ref
  });

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        width: "max-content",
        height: "max-content",
        top: `${top}px`,
        left: `${left}px`,
        opacity: rendered ? 1 : 0,
        zIndex: 100
      }}
      {...popupProps}
    >
      {children({ ...rest })}
    </div>
  );
}
